import type { CSSProperties } from 'react';
import { PortalLink } from '../../portal/PortalLink';
import { isVisible, t } from '../../portal/i18n';
import { BlockIcon, guessSidebarIcon } from '../icons/sidebarIcons';
import type { HeaderAction, HeaderConfig } from '../../portal/shellTypes';
import type { CurrentUser } from '../../portal/auth-types';
import type { HeaderProps } from './index';
import { CartQuickView } from '../widgets/CartQuickView';

/**
 * Render the header's right-cluster action buttons.
 *
 * <p>Strategy:
 * <ul>
 *   <li>Render every entry in {@code config.header.actions[]} in order.
 *       Empty / undefined renders nothing.</li>
 * </ul>
 *
 * <p>Each row:
 * <ul>
 *   <li>Gates on {@code requireAuth} + {@code requirePerms} via the
 *       shared {@link isVisible} helper (same logic the menu uses).</li>
 *   <li>Translates the label with the current locale + i18n fallback.</li>
 *   <li>Resolves the icon string against the sidebar-icon catalog;
 *       when omitted, renders label-only.</li>
 *   <li>Variant {@code primary} → filled brand button,
 *       {@code secondary} → outlined,
 *       {@code ghost} → plain link. Defaults to {@code ghost}.</li>
 *   <li>Target {@code new-tab} → opens in a new tab with
 *       {@code rel="noopener noreferrer"}.</li>
 * </ul>
 */
export interface HeaderActionsProps {
  header: HeaderConfig;
  locale: string;
  i18nDefault: string;
  currentUser: CurrentUser | null;
  tenantHref: HeaderProps['tenantHref'];
}

export function HeaderActions({
  header,
  locale,
  i18nDefault,
  currentUser,
  tenantHref,
}: HeaderActionsProps) {
  const resolved = resolveActions(header);
  if (resolved.length === 0) return null;

  return (
    <>
      {resolved.map((action, idx) => {
        const visible = isVisible(
          {
            requireAuth: action.requireAuth,
            requirePerms: action.requirePerms,
            requireDynamicGroupIds: action.requireDynamicGroupIds,
            requireSecurityGroupIds: action.requireSecurityGroupIds,
          },
          currentUser,
        );
        if (!visible) return null;

        const label = t(action.label, locale, i18nDefault);
        if (!label) return null;

        const variant = action.variant ?? 'ghost';
        const target = action.target === 'new-tab' ? '_blank' : undefined;
        const rel = target === '_blank' ? 'noopener noreferrer' : undefined;
        // Always resolve a fallback icon (even for `primary`) so the
        // tablet icon-only collapse (see .lms-header-action-icon in
        // portal.css, mirrors .lms-menu-icon) has something to show.
        const iconKey = action.icon || guessSidebarIcon(label);

        if (action.href === '/cart') {
          return (
            <CartQuickView
              key={`${idx}::${action.href}`}
              label={label}
              cartHref={tenantHref(action.href)}
              checkoutHref={tenantHref('/checkout')}
              shopHref={tenantHref('/categories')}
              icon={iconKey}
              style={variantStyle(variant)}
            />
          );
        }

        // Actions are positional; the canonical key would be
        // `${label}::${href}` but those can repeat (two "Sign in" links
        // to different targets in different auth states is a legit
        // pattern), so the index is safer here.
        return (
          <PortalLink
            key={`${idx}::${action.href}`}
            href={tenantHref(action.href)}
            target={target}
            rel={rel}
            className="lms-header-action-btn lms-header-utility-btn"
            style={variantStyle(variant)}
            // The 768–1023px / narrow-container icon-only collapse (see
            // .lms-header-action-label in portal.css) sets `display: none`
            // on the visible label, which removes it from the a11y tree
            // too — without this, the link would have no discernible
            // name at that breakpoint since the icon span is aria-hidden.
            aria-label={label}
          >
            {iconKey ? (
              <span
                aria-hidden
                className="lms-header-action-icon"
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <BlockIcon value={iconKey} size={20} />
              </span>
            ) : null}
            <span className="lms-header-action-label">{label}</span>
          </PortalLink>
        );
      })}
    </>
  );
}

// -----------------------------------------------------------------
// Internals
// -----------------------------------------------------------------

function resolveActions(header: HeaderConfig): HeaderAction[] {
  return header.actions ?? [];
}

function variantStyle(variant: NonNullable<HeaderAction['variant']>): CSSProperties {
  const base: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    minHeight: 44,
    padding: '0 var(--space-3, 12px)',
    borderRadius: 'var(--radius-md, 8px)',
    fontSize: 'var(--font-size-sm, 14px)',
    fontWeight: 600,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
  };
  switch (variant) {
    case 'primary':
      return {
        ...base,
        background: 'var(--color-brand-500, #3b5bdb)',
        color: 'var(--color-text-on-brand, #fff)',
        border: 'none',
      };
    case 'secondary':
      return {
        ...base,
        background: 'transparent',
        color: 'var(--color-text-primary, #0f1218)',
        border: '1px solid var(--color-neutral-200, #e5e7eb)',
      };
    case 'ghost':
    default:
      return {
        ...base,
        background: 'transparent',
        color: 'var(--color-text-primary, #0f1218)',
        border: 'none',
      };
  }
}
